import { supabase } from './supabase';
import { AuditLog, TipoAcaoAudit } from '../types';

// ========== SERVIÇO DE AUDIT TRAIL ==========

export class AuditService {
  // Registrar ação no audit log
  static async registrarAcao(params: {
    usuarioId: string;
    usuarioNome: string;
    acao: TipoAcaoAudit;
    tabela: string;
    registroId?: string;
    valoresAntigos?: any;
    valoresNovos?: any;
    motivo?: string;
  }): Promise<AuditLog> {
    try {
      const novoLog: Omit<AuditLog, 'id' | 'createdAt' | 'ipAddress' | 'userAgent'> = {
        usuarioId: params.usuarioId,
        usuarioNome: params.usuarioNome,
        acao: params.acao,
        tabela: params.tabela,
        registroId: params.registroId,
        valoresAntigos: params.valoresAntigos,
        valoresNovos: params.valoresNovos,
        motivo: params.motivo
      };

      const { data, error } = await supabase
        .from('audit_logs')
        .insert({
          usuario_id: params.usuarioId,
          usuario_nome: params.usuarioNome,
          acao: params.acao,
          tabela: params.tabela,
          registro_id: params.registroId,
          valores_antigos: params.valoresAntigos,
          valores_novos: params.valoresNovos,
          motivo: params.motivo,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          ip_address: await this.obterIPAddress(),
          user_agent: navigator.userAgent
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao registrar ação no audit log:', error);
      return null as unknown as AuditLog;
    }
  }

  // Buscar logs de audit trail
  static async buscarLogs(filters?: {
    usuarioId?: string;
    acao?: TipoAcaoAudit;
    tabela?: string;
    registroId?: string;
    dataInicio?: string;
    dataFim?: string;
  }): Promise<AuditLog[]> {
    try {
      let query = supabase
        .from('audit_logs')
        .select('*');

      if (filters?.usuarioId) {
        query = query.eq('usuario_id', filters.usuarioId);
      }

      if (filters?.acao) {
        query = query.eq('acao', filters.acao);
      }

      if (filters?.tabela) {
        query = query.eq('tabela', filters.tabela);
      }

      if (filters?.registroId) {
        query = query.eq('registro_id', filters.registroId);
      }

      if (filters?.dataInicio) {
        query = query.gte('created_at', filters.dataInicio);
      }

      if (filters?.dataFim) {
        query = query.lte('created_at', filters.dataFim);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      return [];
    }
  }

  // Buscar histórico de um registro específico
  static async buscarHistoricoRegistro(tabela: string, registroId: string): Promise<AuditLog[]> {
    return this.buscarLogs({ tabela, registroId });
  }

  // Buscar ações de um usuário
  static async buscarAcoesUsuario(usuarioId: string): Promise<AuditLog[]> {
    return this.buscarLogs({ usuarioId });
  }

  // Obter IP address do usuário
  private static async obterIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  }

  // Métodos de conveniência para ações comuns
  static async registrarCriacao(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    valores: any
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.CRIAR,
      tabela,
      registroId,
      valoresNovos: valores
    });
  }

  static async registrarAtualizacao(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    valoresAntigos: any,
    valoresNovos: any
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.ATUALIZAR,
      tabela,
      registroId,
      valoresAntigos,
      valoresNovos
    });
  }

  static async registrarExclusao(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    valores: any
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.DELETAR,
      tabela,
      registroId,
      valoresAntigos: valores
    });
  }

  static async registrarAprovacao(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    motivo?: string
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.APROVAR,
      tabela,
      registroId,
      motivo
    });
  }

  static async registrarReprovacao(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    motivo: string
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.REPROVAR,
      tabela,
      registroId,
      motivo
    });
  }

  static async registrarReagendamento(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    motivo: string
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.REAGENDAR,
      tabela,
      registroId,
      motivo
    });
  }

  static async registrarAnexoDocumento(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    nomeArquivo: string
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.ANEXAR_DOCUMENTO,
      tabela,
      registroId,
      valoresNovos: { nomeArquivo }
    });
  }

  static async registrarCriacaoPedido(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    numeroPedido: string
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.CRIAR_PEDIDO,
      tabela,
      registroId,
      valoresNovos: { numeroPedido }
    });
  }

  static async registrarComentario(
    usuarioId: string,
    usuarioNome: string,
    tabela: string,
    registroId: string,
    comentario: string
  ): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.COMENTAR,
      tabela,
      registroId,
      valoresNovos: { comentario }
    });
  }

  static async registrarLogin(usuarioId: string, usuarioNome: string): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.LOGIN,
      tabela: 'usuarios'
    });
  }

  static async registrarLogout(usuarioId: string, usuarioNome: string): Promise<AuditLog> {
    return this.registrarAcao({
      usuarioId,
      usuarioNome,
      acao: TipoAcaoAudit.LOGOUT,
      tabela: 'usuarios'
    });
  }
}

export default AuditService;
